import React, { useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Language as LanguageIcon,
  Settings as SettingsIcon,
  Pages as PagesIcon,
  Menu as MenuIcon,
  BarChart as BarChartIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchMicroSiteDetails } from '../store/actions/microSiteActions';
import FileManagement from '../components/FileManagement';

function MicroSiteOverview() {
  const { accountId, siteId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentSite, isLoading, error } = useSelector((state) => state.microSite);

  useEffect(() => {
    if (accountId && siteId) {
      dispatch(fetchMicroSiteDetails({ accountId, siteId }));
    }
  }, [accountId, siteId, dispatch]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button onClick={() => navigate(`/accounts/${accountId}/dashboard`)}>
          Return to Account Dashboard
        </Button>
      </Container>
    );
  }

  if (!currentSite) {
    return null;
  }

  const handleVisitSite = () => {
    window.open(`https://${currentSite.subdomain}.${currentSite.domain}`, '_blank');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            {currentSite.name}
          </Typography>
          <Button
            variant="contained"
            onClick={handleVisitSite}
            startIcon={<LanguageIcon />}
          >
            Visit Site
          </Button>
        </Box>
        <Typography color="text.secondary" gutterBottom>
          {currentSite.subdomain}.{currentSite.domain}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <Chip
            label={currentSite.status}
            color={currentSite.status === 'active' ? 'success' : 'default'}
            sx={{ mr: 1 }}
          />
          <Chip
            label={currentSite.theme}
            variant="outlined"
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PagesIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Pages</Typography>
            </Box>
            <Typography color="text.secondary" paragraph>
              Manage your site's pages and content
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(`/accounts/${accountId}/sites/${siteId}/pages`)}
            >
              Manage Pages
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <MenuIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Navigation</Typography>
            </Box>
            <Typography color="text.secondary" paragraph>
              Configure site menus and navigation
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(`/accounts/${accountId}/sites/${siteId}/navigation`)}
            >
              Manage Navigation
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <SettingsIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Settings</Typography>
            </Box>
            <Typography color="text.secondary" paragraph>
              Configure site settings and preferences
            </Typography>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => navigate(`/accounts/${accountId}/sites/${siteId}/settings`)}
            >
              Site Settings
            </Button>
          </Paper>
        </Grid>
      </Grid>
      <Divider sx={{ my: 4 }} />
      <FileManagement accountId={accountId} />
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          component={Link}
          to={`/accounts/${accountId}/sites/${siteId}/edit`}
        >
          Edit
        </Button>
        <Button
          variant="outlined"
          startIcon={<BarChartIcon />}
          component={Link}
          to={`/account/${accountId}/microsites/${siteId}/analytics`}
        >
          Analytics
        </Button>
      </Box>
    </Container>
  );
}

export default MicroSiteOverview; 